import { IoMenu, IoCartOutline, IoLocationOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import classNames from "classnames";
import Logo from "../common/Logo";
import Button from "../common/Button";
import SideMenu from "./SideMenu";
import Modal from "../common/Modal";
import AuthButton from "../features/AuthButton";
import { formatPrice } from "../../utils/helpers";
import RestaurantSearchbar from "../features/RestaurantSearchbar";
import OrderTypeDropDown from "../features/OrderTypeDropDown";
import { DELIVERY_OPTIONS } from "../../configs";
import { useDimensions, useHeaderLogic } from "../../hooks";
import AddressSearchbar from "../features/AddressSearchbar";

const HeaderMobile = ({
  logic,
}: {
  logic: ReturnType<typeof useHeaderLogic>;
}) => {
  const {
    showSideMenu,
    setShowSideMenu,
    showAddressModal,
    setShowAddressModal,
    menuRef,
    address,
    orderType,
    cartItemsTotalPrice,
    isCartDisabled,
    handleOnRestaurantSelect,
    handleOrdertypeSelection,
    handleAddressSelection,
  } = logic;

  const btnClasses = classNames(
    "bg-primary p-1 rounded text-white font-semibold text-sm"
  );

  return (
    <header className="fixed top-0 w-full z-[999] flex items-center p-1 bg-white shadow-md">
      {/* Left */}
      <IoMenu
        className="w-7 h-7 cursor-pointer"
        onClick={() => setShowSideMenu(true)}
      />

      {/* Address */}
      <div
        className="flex items-center gap-2 ml-3 cursor-pointer border-b-2 border-secondary"
        onClick={() => setShowAddressModal(true)}
      >
        <IoLocationOutline className="text-lg text-primary" />
        <span className="text-xs truncate max-w-2/3">
          {address?.displayName || "Saisissez votre adresse"}
        </span>
      </div>

      {/* Cart */}
      <div className="ml-auto">
        <Link to="/checkout">
          <Button
            className={`${btnClasses} flex items-center gap-1`}
            disabled={isCartDisabled}
          >
            <IoCartOutline className="text-lg" />
            <span>{formatPrice(cartItemsTotalPrice)}</span>
          </Button>
        </Link>
      </div>

      {/* SideMenu */}
      <SideMenu
        ref={menuRef}
        show={showSideMenu}
        onClose={() => setShowSideMenu(false)}
      />

      {/* Modal */}
      {showAddressModal && (
        <Modal onClose={() => setShowAddressModal(false)}>
          <div className="flex flex-col gap-4 bg-white p-4 rounded bg-white z-40">
            <OrderTypeDropDown
              id="orderType"
              value={orderType}
              options={DELIVERY_OPTIONS}
              setFieldValue={handleOrdertypeSelection}
              className="shadow-lg h-10 border"
            />

            <AddressSearchbar
              value={address}
              setAddress={handleAddressSelection}
              placeholder="Saisissez votre adresse"
              className="h-10 border"
            />

            {address && (
              <RestaurantSearchbar
                orderType={orderType}
                address={address}
                onSelect={handleOnRestaurantSelect}
                placeholder="Chercher un restaurant"
                className="min-w-full"
              />
            )}
          </div>
        </Modal>
      )}
    </header>
  );
};

const HeaderDesktop = ({
  logic,
}: {
  logic: ReturnType<typeof useHeaderLogic>;
}) => {
  const {
    showSideMenu,
    setShowSideMenu,
    menuRef,
    address,
    orderType,
    cartItemsTotalPrice,
    isCartDisabled,
    handleOnRestaurantSelect,
    handleOrdertypeSelection,
    handleAddressSelection,
  } = logic;

  const btnClasses = classNames(
    "bg-primary px-3 py-2 rounded text-white font-semibold"
  );

  return (
    <header className="fixed w-full flex items-center gap-4 px-4 py-2 shadow-md top-0 w-full z-[999] bg-white">
      {/* Left: SideMenu + Logo */}
      <div className="flex items-center gap-3">
        <IoMenu
          className="w-7 h-7 cursor-pointer"
          onClick={() => setShowSideMenu(true)}
        />
        <Logo className="w-10" />
      </div>

      {/* Middle: OrderType + Address + Restaurant Search */}
      <div className="flex items-center gap-3 flex-1 ml-4">
        <OrderTypeDropDown
          id="orderType"
          value={orderType}
          options={DELIVERY_OPTIONS}
          setFieldValue={handleOrdertypeSelection}
          className="shadow-lg h-10 border min-w-40"
        />

        <AddressSearchbar
          value={address}
          setAddress={handleAddressSelection}
          placeholder="Adresse"
          className="h-10 min-w-64 border"
        />

        {address && (
          <RestaurantSearchbar
            orderType={orderType}
            address={address}
            onSelect={handleOnRestaurantSelect}
            placeholder="Chercher un restaurant"
            className="min-w-72"
          />
        )}
      </div>

      {/* Right: Auth + Cart */}
      <div className="flex items-center gap-3">
        <AuthButton />

        <Link to="/checkout">
          <Button
            className={`${btnClasses} flex items-center gap-2 p-3`}
            disabled={isCartDisabled}
          >
            <IoCartOutline className="text-xl" />
            <span>{formatPrice(cartItemsTotalPrice)}</span>
          </Button>
        </Link>
      </div>

      {/* SideMenu */}
      <SideMenu
        ref={menuRef}
        show={showSideMenu}
        onClose={() => setShowSideMenu(false)}
      />
    </header>
  );
};

const Header = () => {
  const { isDesktop } = useDimensions();
  const logic = useHeaderLogic();

  return isDesktop ? (
    <HeaderDesktop logic={logic} />
  ) : (
    <HeaderMobile logic={logic} />
  );
};

export default Header;
